import React, { useEffect, useMemo, useState } from 'react';
import Button from '../ui/Button';
import DateSelect from '../ui/DateSelect';
import StickyHeader from './StickyHeader';
import SafeguardingBanner from './SafeguardingBanner';
import AttendanceSummaryCard from './AttendanceSummaryCard';
import AttendanceTrendChart from './AttendanceTrendChart';
import WeeklyGroupedLog from './WeeklyGroupedLog';
import SafeguardingContextPanel from './SafeguardingContextPanel';
import { formatDate, formatMonthDay } from '../../utils/dateFormat';

const STORAGE_KEY = 'safeguardpro_attendance_followups_v1';
// TODO(API): Persist attendance follow-up state on the student record.
// Proposed contract:
// student.attendanceFollowUp = {
//   ownerId: string,
//   ownerName: string,
//   reviewDate: string (YYYY-MM-DD),
//   note: string,
//   actionPlan: string,
//   reviewStartedAt: string (ISO)
// };
// student.attendanceThreshold = number

const loadFollowup = (studentId) => {
  if (typeof window === 'undefined') {
    return {
      owner: '',
      reviewDate: '',
      note: '',
      actionPlan: '',
      reviewStarted: false
    };
  }
  try {
    const raw = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}');
    return {
      owner: '',
      reviewDate: '',
      note: '',
      actionPlan: '',
      reviewStarted: false,
      ...(raw[studentId] || {})
    };
  } catch (error) {
    return {
      owner: '',
      reviewDate: '',
      note: '',
      actionPlan: '',
      reviewStarted: false
    };
  }
};

const saveFollowup = (studentId, nextState) => {
  if (typeof window === 'undefined') return;
  try {
    const raw = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}');
    raw[studentId] = nextState;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(raw));
  } catch (error) {
    // TODO: replace with API update when backend supports attendance follow-up state.
  }
};

const parseDateValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return null;
  return date;
};

const startOfWeek = (date) => {
  const day = date.getDay();
  const diff = (day + 6) % 7;
  const start = new Date(date);
  start.setDate(date.getDate() - diff);
  start.setHours(0, 0, 0, 0);
  return start;
};

const buildWeekLabels = (count) => {
  const today = new Date();
  const currentStart = startOfWeek(today);
  return Array.from({ length: count }, (_, idx) => {
    const weekStart = new Date(currentStart);
    weekStart.setDate(currentStart.getDate() - (count - 1 - idx) * 7);
    return formatMonthDay(weekStart);
  });
};

export default function StudentAttendanceView({
  student,
  attendanceThreshold,
  incidentReports = [],
  onMarkAttendance,
  onViewIncidents
}) {
  const attendance = student.attendance || {};
  const threshold = Number(attendanceThreshold || 70);
  const rate = attendance.rate ?? 0;

  const [timeRange, setTimeRange] = useState('8weeks');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [followup, setFollowup] = useState(() => loadFollowup(student.id));
  const [ownerDraft, setOwnerDraft] = useState(followup.owner);
  const [reviewDraft, setReviewDraft] = useState(followup.reviewDate);
  const [noteDraft, setNoteDraft] = useState(followup.note);
  const [planDraft, setPlanDraft] = useState(followup.actionPlan);
  const [showOwnerEditor, setShowOwnerEditor] = useState(false);
  const [showReviewEditor, setShowReviewEditor] = useState(false);
  const [showNoteEditor, setShowNoteEditor] = useState(false);
  const [showPlanEditor, setShowPlanEditor] = useState(false);

  useEffect(() => {
    saveFollowup(student.id, followup);
  }, [followup, student.id]);

  const status = useMemo(() => {
    const critical =
      student.riskLevel === 'critical' || rate < threshold - 20 || (student.riskLevel === 'high' && rate < threshold);
    const atRisk = !critical && rate < threshold;
    return {
      label: critical ? 'Critical' : atRisk ? 'At risk' : 'OK',
      variant: critical ? 'danger' : atRisk ? 'review' : 'active',
      critical,
      atRisk
    };
  }, [student.riskLevel, rate, threshold]);

  const activityStatus = student.status === 'Archived'
    ? { label: 'Archived', variant: 'inactive' }
    : { label: 'Active', variant: 'active' };

  const records = useMemo(() => {
    const logs = attendance.logs || [];
    const reasons = attendance.absenceReasons || [];
    const mappedLogs = logs.map((log, idx) => {
      const statusValue = (log.status || 'attended').toLowerCase();
      const isAbsence = statusValue.includes('absence');
      const unauthorised = statusValue.includes('unauthorised') || statusValue.includes('unauthorized');
      const category = isAbsence ? 'absence' : 'attended';
      return {
        id: log.id || `log-${idx}-${log.date}`,
        date: log.date || log.recordedAt || '',
        session: log.programme || 'Session',
        statusLabel: log.status || 'attended',
        reason: log.notes || '',
        recordedBy: log.recordedBy || log.recordedByName || '',
        category,
        unauthorised,
        badgeLabel: log.status || 'attended',
        badgeVariant: isAbsence ? (unauthorised ? 'danger' : 'review') : 'active'
      };
    });
    const mappedReasons = reasons.map((reason, idx) => ({
      id: `reason-${idx}-${reason.date}`,
      date: reason.date || '',
      session: reason.programme || 'Session',
      statusLabel: 'authorised absence',
      reason: reason.reason || '',
      recordedBy: reason.recordedBy || '',
      category: 'absence',
      unauthorised: false,
      badgeLabel: 'authorised absence',
      badgeVariant: 'review'
    }));
    return [...mappedLogs, ...mappedReasons]
      .filter((item) => item.date)
      .sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [attendance.logs, attendance.absenceReasons]);

  const stats = useMemo(() => {
    const attended = records.filter((r) => r.category === 'attended').length;
    const authorised = records.filter((r) => r.category === 'absence' && !r.unauthorised).length;
    const unauthorised = records.filter((r) => r.unauthorised).length;
    return { attended, authorised, unauthorised };
  }, [records]);

  const recentAbsences = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 14);
    return records.filter((r) => {
      if (r.category !== 'absence') return false;
      const date = parseDateValue(r.date);
      return date && date >= cutoff;
    }).length;
  }, [records]);

  const chartMeta = useMemo(() => {
    const points = attendance.last8Weeks || [];
    if (!points.length) return [];
    const labels = buildWeekLabels(points.length);
    const counts = points.map(() => ({ sessions: 0, absences: 0 }));
    const currentWeekStart = startOfWeek(new Date());
    records.forEach((record) => {
      const date = parseDateValue(record.date);
      if (!date) return;
      const recordWeek = startOfWeek(date);
      const diffWeeks = Math.round((currentWeekStart - recordWeek) / (7 * 24 * 60 * 60 * 1000));
      const index = points.length - 1 - diffWeeks;
      if (index < 0 || index >= points.length) return;
      counts[index].sessions += 1;
      if (record.category === 'absence') counts[index].absences += 1;
    });
    return points.map((value, idx) => ({
      label: labels[idx] || `Week ${idx + 1}`,
      percent: value,
      sessions: counts[idx].sessions,
      absences: counts[idx].absences
    }));
  }, [attendance.last8Weeks, records]);

  const reviewDateValid = useMemo(() => {
    if (!followup.reviewDate) return false;
    const date = parseDateValue(followup.reviewDate);
    if (!date) return false;
    const now = new Date();
    const diffHours = (date - now) / (1000 * 60 * 60);
    return diffHours <= 48 && diffHours >= 0;
  }, [followup.reviewDate]);

  const actions = [
    {
      id: 'assign-owner',
      label: 'Assign safeguarding lead',
      helper: followup.owner ? `Assigned to ${followup.owner}` : 'No owner assigned yet.',
      completed: Boolean(followup.owner),
      actionLabel: 'Assign',
      onAction: () => setShowOwnerEditor(true),
      editor: showOwnerEditor && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            className="sp-input"
            value={ownerDraft}
            onChange={(event) => setOwnerDraft(event.target.value)}
            placeholder="Enter safeguarding lead"
            aria-label="Safeguarding lead name"
          />
          <Button
            size="sm"
            variant="secondary"
            type="button"
            onClick={() => {
              const next = ownerDraft.trim();
              if (!next) return;
              setFollowup((prev) => ({ ...prev, owner: next }));
              setShowOwnerEditor(false);
            }}
          >
            Save
          </Button>
          <Button
            size="sm"
            variant="ghost"
            type="button"
            onClick={() => {
              setOwnerDraft(followup.owner);
              setShowOwnerEditor(false);
            }}
          >
            Cancel
          </Button>
        </div>
      )
    },
    {
      id: 'set-review-date',
      label: 'Set review date (within 48 hours)',
      helper: followup.reviewDate ? `Review due ${formatDate(followup.reviewDate)}` : 'No review date set.',
      completed: reviewDateValid,
      actionLabel: 'Set date',
      onAction: () => setShowReviewEditor(true),
      editor: showReviewEditor && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <DateSelect
            value={reviewDraft}
            onChange={setReviewDraft}
            className="w-full"
          />
          <Button
            size="sm"
            variant="secondary"
            type="button"
            onClick={() => {
              if (!reviewDraft) return;
              setFollowup((prev) => ({ ...prev, reviewDate: reviewDraft }));
              setShowReviewEditor(false);
            }}
          >
            Save
          </Button>
          <Button
            size="sm"
            variant="ghost"
            type="button"
            onClick={() => {
              setReviewDraft(followup.reviewDate);
              setShowReviewEditor(false);
            }}
          >
            Cancel
          </Button>
        </div>
      )
    },
    {
      id: 'add-note',
      label: 'Add note / rationale',
      helper: followup.note ? 'Note captured.' : 'No rationale recorded.',
      completed: Boolean(followup.note),
      actionLabel: 'Add note',
      onAction: () => setShowNoteEditor(true),
      editor: showNoteEditor && (
        <div className="mt-3 space-y-2">
          <textarea
            className="sp-textarea w-full"
            rows={3}
            value={noteDraft}
            onChange={(event) => setNoteDraft(event.target.value)}
            placeholder="Add safeguarding rationale..."
            aria-label="Safeguarding rationale"
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              type="button"
              onClick={() => {
                const next = noteDraft.trim();
                if (!next) return;
                setFollowup((prev) => ({ ...prev, note: next }));
                setShowNoteEditor(false);
              }}
            >
              Save
            </Button>
            <Button
              size="sm"
              variant="ghost"
              type="button"
              onClick={() => {
                setNoteDraft(followup.note);
                setShowNoteEditor(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )
    },
    {
      id: 'action-plan',
      label: 'Create action plan',
      helper: followup.actionPlan ? 'Action plan recorded.' : 'No action plan added.',
      completed: Boolean(followup.actionPlan),
      actionLabel: 'Create plan',
      onAction: () => setShowPlanEditor(true),
      editor: showPlanEditor && (
        <div className="mt-3 space-y-2">
          <textarea
            className="sp-textarea w-full"
            rows={3}
            value={planDraft}
            onChange={(event) => setPlanDraft(event.target.value)}
            placeholder="Outline actions, owners, and deadlines..."
            aria-label="Action plan"
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              type="button"
              onClick={() => {
                const next = planDraft.trim();
                if (!next) return;
                setFollowup((prev) => ({ ...prev, actionPlan: next }));
                setShowPlanEditor(false);
              }}
            >
              Save
            </Button>
            <Button
              size="sm"
              variant="ghost"
              type="button"
              onClick={() => {
                setPlanDraft(followup.actionPlan);
                setShowPlanEditor(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )
    }
  ];

  const ownerEditor = showOwnerEditor ? (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <input
        className="sp-input"
        value={ownerDraft}
        onChange={(event) => setOwnerDraft(event.target.value)}
        placeholder="Assign safeguarding lead"
        aria-label="Assign safeguarding lead"
      />
      <Button
        size="sm"
        variant="secondary"
        type="button"
        onClick={() => {
          const next = ownerDraft.trim();
          if (!next) return;
          setFollowup((prev) => ({ ...prev, owner: next }));
          setShowOwnerEditor(false);
        }}
      >
        Save
      </Button>
      <Button
        size="sm"
        variant="ghost"
        type="button"
        onClick={() => {
          setOwnerDraft(followup.owner);
          setShowOwnerEditor(false);
        }}
      >
        Cancel
      </Button>
    </div>
  ) : null;

  const reviewEditor = showReviewEditor ? (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <DateSelect
        value={reviewDraft}
        onChange={setReviewDraft}
        className="w-full"
      />
      <Button
        size="sm"
        variant="secondary"
        type="button"
        onClick={() => {
          if (!reviewDraft) return;
          setFollowup((prev) => ({ ...prev, reviewDate: reviewDraft }));
          setShowReviewEditor(false);
        }}
      >
        Save
      </Button>
      <Button
        size="sm"
        variant="ghost"
        type="button"
        onClick={() => {
          setReviewDraft(followup.reviewDate);
          setShowReviewEditor(false);
        }}
      >
        Cancel
      </Button>
    </div>
  ) : null;

  const exportSummary = () => {
    window.alert('PDF export coming soon. TODO: connect to reporting API.');
  };

  const safeguardingEvents = [
    ...(student.safeguardingEvents || []).map((event) => ({ ...event, category: 'Safeguarding' })),
    ...(student.medicalEvents || []).map((event) => ({ ...event, category: 'Medical' })),
    ...(student.policeEvents || []).map((event) => ({ ...event, category: 'Police' }))
  ].sort((a, b) => (a.date < b.date ? 1 : -1));

  const openIncidents = incidentReports.filter((report) => report.status === 'open').length;

  const statusHistory = (student.statusHistory || []).map((status) => ({
    status: status.status,
    date: status.date,
    by: status.by
  }));

  return (
    <div className="space-y-6">
      <StickyHeader
        studentName={student.name}
        age={student.age}
        statusLabel={status.label}
        statusVariant={status.variant}
        activityLabel={activityStatus.label}
        activityVariant={activityStatus.variant}
        assignedTo={followup.owner}
        reviewDate={followup.reviewDate}
        onStartReview={() => {
          setFollowup((prev) => ({ ...prev, reviewStarted: true }));
          setShowPlanEditor(true);
        }}
        onAssignOwner={() => setShowOwnerEditor(true)}
        onSetReviewDate={() => setShowReviewEditor(true)}
        onExport={exportSummary}
        ownerEditor={ownerEditor}
        reviewEditor={reviewEditor}
      />

      <SafeguardingBanner
        isCritical={status.critical}
        rate={rate}
        threshold={threshold}
        actions={actions}
      />

      <div className="grid lg:grid-cols-[1fr_320px] gap-4">
        <div className="space-y-4">
          <div className="grid lg:grid-cols-2 gap-4">
            <AttendanceSummaryCard
              rate={rate}
              threshold={threshold}
              delta={rate - threshold}
              statusLabel={status.label}
              statusVariant={status.variant}
              timeRange={timeRange}
              onRangeChange={setTimeRange}
              customRange={customRange}
              onCustomRangeChange={setCustomRange}
              stats={stats}
              recentAbsences={recentAbsences}
            />
            <AttendanceTrendChart
              points={attendance.last8Weeks || []}
              threshold={threshold}
              meta={chartMeta}
            />
          </div>

          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="sp-value" style={{ fontWeight: 600 }}>Attendance Evidence</p>
              <p className="text-xs sp-muted">Review attendance logs and add notes.</p>
            </div>
            {onMarkAttendance && (
              <Button variant="secondary" size="sm" onClick={onMarkAttendance} type="button">
                Record attendance
              </Button>
            )}
          </div>

          <WeeklyGroupedLog
            records={records}
            storageKey={`attendance-notes-${student.id}`}
          />
        </div>

        <SafeguardingContextPanel
          flags={safeguardingEvents}
          incidentsCount={openIncidents}
          onViewIncidents={onViewIncidents}
          notes={student.notesSorted || student.notes || []}
          auditHistory={statusHistory}
        />
      </div>
    </div>
  );
}
